import React, {FC, useEffect} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Col, Modal, ModalBody, ModalHeader, ModalTitle, Row} from 'react-bootstrap'
import {ErrorMessage, Form, Formik} from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import {assignSupplierToCustomHotel} from '../../../../setup/redux/bookings/actions'
import {getCurrencyDropdown, getSuppliers} from '../../../../setup/redux/suppliers/actions'
import {RootState} from '../../../../setup'

export interface ManageCustomHotelSupplierProps {
  show: boolean
  hide: () => void
  customHotel: any
  refetch: () => void
}

export const ManageCustomHotelSupplier: FC<ManageCustomHotelSupplierProps> = ({
  show,
  hide,
  customHotel,
  refetch,
}) => {
  const dispatch = useDispatch()
  const isEditing = Boolean(customHotel?.supplier)

  useEffect(() => {
    // Custom hotels have no service-type supplier lookup -- suppliers are scoped by city.
    dispatch(getSuppliers('city', customHotel?.city?.id))
    dispatch(getCurrencyDropdown())
  }, [])

  const {supplierLists, currenciesDropdown}: any = useSelector<RootState>(
    (state) => ({
      supplierLists: state.suppliers.supplierLists,
      currenciesDropdown: state.suppliers.currenciesDropdown,
    }),
    shallowEqual
  )

  // customHotel.currency arrives as an entity ({id, name}) from the dashboard
  // query, or as a bare name string from the booking detail page.
  const selectedCurrencyOption = currenciesDropdown.find((c: any) =>
    customHotel?.currency && typeof customHotel.currency === 'object'
      ? Number(c.value) === Number(customHotel.currency?.id)
      : c.name === customHotel?.currency
  )

  const initialValues = {
    custom_hotel_id: customHotel.id,
    supplier_id: customHotel?.supplier?.id ?? '',
    purchase_currency_id: selectedCurrencyOption?.value ?? '',
    purchase_price: customHotel?.purchase_price_original ?? '',
  }

  const validationSchema = Yup.object().shape({
    supplier_id: Yup.string().required('Supplier is required'),
  })

  return (
    <Modal show={show} centered={true} onHide={hide}>
      <ModalHeader closeButton>
        <ModalTitle>{isEditing ? 'Update Supplier' : 'Assign Supplier'}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            await dispatch(assignSupplierToCustomHotel(values))
            refetch()
            hide()
          }}
        >
          {({values, handleChange, handleBlur, handleSubmit}) => (
            <React.Fragment>
              <Form className='form'>
                <Row className={'px-2'}>
                  <Col md={12}>
                    <div className={'form-group mb-3'}>
                      <label className='form-label'>
                        Supplier <span className='text-danger'>*</span>
                      </label>
                      <Select
                        options={supplierLists}
                        value={
                          supplierLists.find(
                            (supplier: {value: string}) =>
                              Number(supplier.value) === Number(values.supplier_id)
                          ) || null
                        }
                        name='supplier_id'
                        className='react-select-container'
                        classNamePrefix='react-select'
                        placeholder='Select a supplier'
                        onChange={(option: any) => {
                          handleChange({target: {name: 'supplier_id', value: option?.value}})
                        }}
                        onBlur={() => handleBlur({target: {name: 'supplier_id'}})}
                      />
                      <ErrorMessage
                        name='supplier_id'
                        component='div'
                        className='text-danger mt-2'
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className={'form-group mb-3'}>
                      <label className='form-label'>Purchase Currency</label>
                      <Select
                        options={currenciesDropdown}
                        className='react-select-container'
                        classNamePrefix='react-select'
                        value={
                          currenciesDropdown.find(
                            (currency: any) =>
                              Number(currency.value) === Number(values.purchase_currency_id)
                          ) || null
                        }
                        name='purchase_currency_id'
                        placeholder='Select a currency'
                        onChange={(option: any) => {
                          handleChange({
                            target: {name: 'purchase_currency_id', value: option?.value || ''},
                          })
                        }}
                        onBlur={() => handleBlur({target: {name: 'purchase_currency_id'}})}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className={'form-group mb-3'}>
                      <label className='form-label'>Purchase Price</label>
                      <input
                        type='number'
                        step='0.01'
                        name='purchase_price'
                        className='form-control'
                        placeholder='Enter price'
                        value={values.purchase_price}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className={'float-end'}>
                  <div className='col-md-12 '>
                    <div className='form-group'>
                      <button
                        type='button'
                        name='submit'
                        className='btn btn-primary me-2'
                        onClick={() => handleSubmit()}
                      >
                        {isEditing ? 'Update' : 'Assign Now'}
                      </button>
                    </div>
                  </div>
                </Row>
              </Form>
            </React.Fragment>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}
